import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import "./navbar.css";
import { GoogleLogin } from '@react-oauth/google';
import { createPortal } from "react-dom";
import axios from 'axios';

const PLAN_DETAILS = {
    trial: { label: "Trial", price: "₹2", desc: "1 Day Access" },
    weekly: { label: "Weekly", price: "₹49", desc: "7 Days Access" },
    monthly: { label: "Monthly", price: "₹149", desc: "30 Days Access" },
    yearly: { label: "Yearly", price: "₹799", desc: "365 Days Access" },
};

function Navbar({ baseUrl }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [user, setUser] = useState(null);
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [planLoading, setPlanLoading] = useState(null);
    const [planError, setPlanError] = useState("");
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [authTab, setAuthTab] = useState("login");
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState("");
    const [loginForm, setLoginForm] = useState({ email: "", password: "" });
    const [registerForm, setRegisterForm] = useState({ name: "", email: "", password: "" });

    // Navbar scroll
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () =>
            window.removeEventListener('scroll', handleScroll);
    }, []);

    // Load user from sessionStorage
    useEffect(() => {
        const storedUser = sessionStorage.getItem("user");

        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (error) {
                console.error("Invalid stored user:", error);
                sessionStorage.removeItem("user");
            }
        }
    }, []);

    // Store logged-in user/token and close the auth modal
    const persistLoggedInUser = (loggedInUser, token) => {
        if (!loggedInUser) {
            throw new Error("User data not received from server");
        }

        sessionStorage.setItem("user", JSON.stringify(loggedInUser));

        if (token) {
            sessionStorage.setItem("token", token);
        }

        setUser(loggedInUser);
        setShowModal(false);
        setAuthError("");
    };

    // Google Login Success
    const handleSuccess = async (credentialResponse) => {
        try {
            const credential = credentialResponse.credential;

            if (!credential) {
                throw new Error("Google credential not received");
            }

            // Decode Google JWT payload
            // Google credential is a JWT:
            // header.payload.signature
            const parts = credential.split(".");

            if (parts.length !== 3) {
                throw new Error("Invalid Google credential");
            }

            const base64Url = parts[1];

            const base64 = base64Url
                .replace(/-/g, "+")
                .replace(/_/g, "/");

            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split("")
                    .map(
                        (char) =>
                            "%" +
                            ("00" + char.charCodeAt(0).toString(16)).slice(-2)
                    )
                    .join("")
            );

            const googleUser = JSON.parse(jsonPayload);

            const name = googleUser.name;
            const email = googleUser.email;

            if (!name || !email) {
                throw new Error(
                    "Name and email not found in Google credential"
                );
            }

            console.log("Google User:", {
                name,
                email
            });

            // Send Google token + name + email to backend
            const response = await axios.post(
                `${baseUrl}/api/users/login-with-google`,
                {
                    token: credential,
                    name: name,
                    email: email,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                    },
                }
            );

            console.log("Server Response:", response.data);

            persistLoggedInUser(response.data.user, response.data.token);

        } catch (error) {
            console.error(
                "Login Error:",
                error.response?.data || error.message
            );
        }
    };

    // Email/password login
    const handleEmailLogin = async (e) => {
        e.preventDefault();
        setAuthError("");
        setAuthLoading(true);

        try {
            const response = await axios.post(
                `${baseUrl}/api/users/login`,
                loginForm,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                    },
                }
            );

            persistLoggedInUser(response.data.user, response.data.token);
            setLoginForm({ email: "", password: "" });
        } catch (error) {
            setAuthError(
                error.response?.data?.message ||
                    "Login failed. Please try again."
            );
        } finally {
            setAuthLoading(false);
        }
    };

    // Email/password registration
    const handleRegister = async (e) => {
        e.preventDefault();
        setAuthError("");
        setAuthLoading(true);

        try {
            const response = await axios.post(
                `${baseUrl}/api/users/register`,
                registerForm,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                    },
                }
            );

            persistLoggedInUser(response.data.user, response.data.token);
            setRegisterForm({ name: "", email: "", password: "" });
        } catch (error) {
            setAuthError(
                error.response?.data?.message ||
                    "Registration failed. Please try again."
            );
        } finally {
            setAuthLoading(false);
        }
    };

    // Logout
    const handleLogout = () => {
        sessionStorage.removeItem("user");
        sessionStorage.removeItem("token");
        setUser(null);
        setShowDropdown(false);
    };

    const checkAuthAndOpenModal = () => {
        const storedUser = sessionStorage.getItem("user");

        if (!storedUser) {
            window.dispatchEvent(
                new Event("open-login-modal")
            );

            return false;
        }

        return true;
    };

    // Open login modal event
    useEffect(() => {
        const openLoginModal = () => {
            setShowModal(true);
        };

        window.addEventListener(
            "open-login-modal",
            openLoginModal
        );

        return () => {
            window.removeEventListener(
                "open-login-modal",
                openLoginModal
            );
        };
    }, []);

    // Open plan modal event
    useEffect(() => {
        const openPlanModal = () => {
            setShowPlanModal(true);
        };

        window.addEventListener(
            "open-plan-modal",
            openPlanModal
        );

        return () => {
            window.removeEventListener(
                "open-plan-modal",
                openPlanModal
            );
        };
    }, []);

    const handlePlanSelect = async (plan, gateway) => {
        setPlanError("");

        const storedUser = sessionStorage.getItem("user");

        const userData = storedUser
            ? JSON.parse(storedUser)
            : null;

        if (!userData?.email) {
            setShowPlanModal(false);
            window.dispatchEvent(new Event("open-login-modal"));
            return;
        }

        setSelectedPlan(null);

        if (gateway === "phonepe") {
            await handlePhonepePayment(plan, userData);
        } else {
            await handleRazorpayPayment(plan, userData);
        }
    };

    const handlePhonepePayment = async (plan, userData) => {
        setPlanLoading(plan);

        try {
            const { data: order } = await axios.post(
                `${baseUrl}/api/payment/phonepe/create-order`,
                {
                    plan,
                    email: userData.email,
                }
            );

            if (!order.redirectUrl) {
                throw new Error("No redirect URL received");
            }

            // Full-page redirect to PhonePe's hosted checkout; the user is sent
            // back to /payment-status once they complete or cancel payment.
            window.location.href = order.redirectUrl;
        } catch (error) {
            console.error(
                "PhonePe Payment Error:",
                error.response?.data || error.message
            );
            setPlanError("Could not start payment. Please try again.");
            setPlanLoading(null);
        }
    };

    const handleRazorpayPayment = async (plan, userData) => {
        if (typeof window.Razorpay !== "function") {
            setPlanError("Payment gateway is still loading. Please try again in a moment.");
            return;
        }

        setPlanLoading(plan);

        try {
            const { data: order } = await axios.post(
                `${baseUrl}/api/payment/razorpay/create-order`,
                {
                    plan,
                    email: userData.email,
                }
            );

            const checkout = new window.Razorpay({
                key: order.keyId,
                amount: order.amount,
                currency: order.currency,
                order_id: order.orderId,
                name: "Dharmiq",
                description: `${plan.charAt(0).toUpperCase()}${plan.slice(1)} Plan`,
                prefill: {
                    name: userData.name || "",
                    email: userData.email,
                },
                theme: { color: "#ff6b00" },
                handler: async (response) => {
                    try {
                        const verifyRes = await axios.post(
                            `${baseUrl}/api/payment/razorpay/verify`,
                            {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                email: userData.email,
                            }
                        );

                        if (verifyRes.data.user) {
                            sessionStorage.setItem(
                                "user",
                                JSON.stringify(verifyRes.data.user)
                            );
                            setUser(verifyRes.data.user);
                        }

                        setShowPlanModal(false);
                    } catch (verifyError) {
                        console.error(
                            "Payment verification failed:",
                            verifyError.response?.data || verifyError.message
                        );
                        setPlanError("We couldn't confirm your payment. Please contact support if the amount was deducted.");
                    } finally {
                        setPlanLoading(null);
                    }
                },
                modal: {
                    ondismiss: () => {
                        setPlanLoading(null);
                    },
                },
            });

            checkout.on("payment.failed", (response) => {
                console.error("Payment failed:", response.error);
                setPlanError("Payment failed. Please try again.");
                setPlanLoading(null);
            });

            checkout.open();
        } catch (error) {
            console.error(
                "Payment Error:",
                error.response?.data || error.message
            );
            setPlanError("Could not start payment. Please try again.");
            setPlanLoading(null);
        }
    };

    return (
        <nav
            className={`navbar-custom ${
                scrolled ? 'scrolled' : ''
            }`}
        >
            <div className="container nav-inner">

                {/* Logo */}
                <div className="logo-container">
                    <Link to="/">
                        <img
                            className="logo"
                            src="/logo/dharmiq_logo-transparent-mini.png"
                            alt="Dharmiq"
                        />
                    </Link>
                </div>

                {/* Menu */}
                <div
                    className={`nav-links ${
                        menuOpen ? "active-menu" : ""
                    }`}
                >

                    <Link
                        to="/"
                        onClick={() => setMenuOpen(false)}
                    >
                        Home
                    </Link>

                    <Link
                        to="/about"
                        onClick={() => setMenuOpen(false)}
                    >
                        About
                    </Link>

                    <Link
                        to="/contact-us"
                        onClick={() => setMenuOpen(false)}
                    >
                        Contact Us
                    </Link>

                    {/* Login / Profile */}
                    {!user ? (
                        <button
                            className="loginBtn"
                            onClick={() => setShowModal(true)}
                        >
                            Login
                        </button>
                    ) : (
                        <div className="profileWrapper">

                            <div
                                className="profileBox"
                                onClick={() =>
                                    setShowDropdown(!showDropdown)
                                }
                            >

                                <img
                                    src={user.picture}
                                    alt={user.name}
                                    className="profileImage"
                                />

                                <span className="profileName">
                                    {user.name}
                                </span>

                            </div>

                            {showDropdown && (
                                <div className="dropdownMenu">

                                    <button
                                        onClick={handleLogout}
                                    >
                                        Logout
                                    </button>

                                </div>
                            )}

                        </div>
                    )}

                </div>

                {/* Mobile Hamburger */}
                <div
                    className={`menu-toggle ${
                        menuOpen ? "open" : ""
                    }`}
                    onClick={() =>
                        setMenuOpen(!menuOpen)
                    }
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </div>

            </div>

            {/* Login Modal */}
            {showModal &&
                createPortal(
                    <div
                        className="modalOverlay"
                        onClick={() =>
                            setShowModal(false)
                        }
                    >
                        <div
                            className="modalBox"
                            onClick={(e) =>
                                e.stopPropagation()
                            }
                        >

                            <button
                                className="closeBtn"
                                onClick={() =>
                                    setShowModal(false)
                                }
                            >
                                ×
                            </button>

                            <div className="authTabs">
                                <button
                                    type="button"
                                    className={`authTab ${
                                        authTab === "login" ? "authTabActive" : ""
                                    }`}
                                    onClick={() => {
                                        setAuthTab("login");
                                        setAuthError("");
                                    }}
                                >
                                    Login
                                </button>

                                <button
                                    type="button"
                                    className={`authTab ${
                                        authTab === "register" ? "authTabActive" : ""
                                    }`}
                                    onClick={() => {
                                        setAuthTab("register");
                                        setAuthError("");
                                    }}
                                >
                                    Register
                                </button>
                            </div>

                            {authTab === "login" ? (
                                <form className="authForm" onSubmit={handleEmailLogin}>
                                    <input
                                        type="email"
                                        placeholder="Email"
                                        value={loginForm.email}
                                        onChange={(e) =>
                                            setLoginForm({
                                                ...loginForm,
                                                email: e.target.value,
                                            })
                                        }
                                        required
                                    />

                                    <input
                                        type="password"
                                        placeholder="Password"
                                        value={loginForm.password}
                                        onChange={(e) =>
                                            setLoginForm({
                                                ...loginForm,
                                                password: e.target.value,
                                            })
                                        }
                                        required
                                    />

                                    {authError && (
                                        <p className="authError">{authError}</p>
                                    )}

                                    <button
                                        type="submit"
                                        className="authSubmitBtn"
                                        disabled={authLoading}
                                    >
                                        {authLoading ? "Logging in..." : "Login"}
                                    </button>
                                </form>
                            ) : (
                                <form className="authForm" onSubmit={handleRegister}>
                                    <input
                                        type="text"
                                        placeholder="Full Name"
                                        value={registerForm.name}
                                        onChange={(e) =>
                                            setRegisterForm({
                                                ...registerForm,
                                                name: e.target.value,
                                            })
                                        }
                                        required
                                    />

                                    <input
                                        type="email"
                                        placeholder="Email"
                                        value={registerForm.email}
                                        onChange={(e) =>
                                            setRegisterForm({
                                                ...registerForm,
                                                email: e.target.value,
                                            })
                                        }
                                        required
                                    />

                                    <input
                                        type="password"
                                        placeholder="Password (min 6 characters)"
                                        value={registerForm.password}
                                        onChange={(e) =>
                                            setRegisterForm({
                                                ...registerForm,
                                                password: e.target.value,
                                            })
                                        }
                                        minLength={6}
                                        required
                                    />

                                    {authError && (
                                        <p className="authError">{authError}</p>
                                    )}

                                    <button
                                        type="submit"
                                        className="authSubmitBtn"
                                        disabled={authLoading}
                                    >
                                        {authLoading
                                            ? "Creating account..."
                                            : "Register"}
                                    </button>
                                </form>
                            )}

                            <div className="authDivider">
                                <span>or</span>
                            </div>

                            <GoogleLogin
                                onSuccess={handleSuccess}
                                onError={() => {
                                    console.log(
                                        "Login Failed"
                                    );
                                }}
                            />

                        </div>
                    </div>,
                    document.body
                )
            }

            {/* Plan Modal */}
            {showPlanModal &&
                createPortal(
                    <div
                        className="modalOverlay"
                        onClick={() => {
                            setShowPlanModal(false);
                            setSelectedPlan(null);
                        }}
                    >
                        <div
                            className="planModal"
                            onClick={(e) =>
                                e.stopPropagation()
                            }
                        >

                            <button
                                className="closeBtn"
                                onClick={() => {
                                    setShowPlanModal(false);
                                    setSelectedPlan(null);
                                }}
                            >
                                ×
                            </button>

                            {selectedPlan ? (
                                <>
                                    <button
                                        type="button"
                                        className="backLink"
                                        onClick={() => setSelectedPlan(null)}
                                    >
                                        ← Back to plans
                                    </button>

                                    <h2>
                                        Choose Payment Method
                                    </h2>

                                    <p className="gatewayChooserPlan">
                                        {PLAN_DETAILS[selectedPlan].label} Plan — {PLAN_DETAILS[selectedPlan].price} ({PLAN_DETAILS[selectedPlan].desc})
                                    </p>

                                    {planError && (
                                        <p className="planError">
                                            {planError}
                                        </p>
                                    )}

                                    <div className="gatewayChooserOptions">
                                        <button
                                            className="gatewayChooserBtn"
                                            disabled={planLoading === selectedPlan}
                                            onClick={() =>
                                                handlePlanSelect(selectedPlan, "razorpay")
                                            }
                                        >
                                            {planLoading === selectedPlan
                                                ? "Processing..."
                                                : "Pay with Razorpay"}
                                        </button>

                                        <button
                                            className="gatewayChooserBtn"
                                            disabled={planLoading === selectedPlan}
                                            onClick={() =>
                                                handlePlanSelect(selectedPlan, "phonepe")
                                            }
                                        >
                                            {planLoading === selectedPlan
                                                ? "Processing..."
                                                : "Pay with PhonePe"}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h2>
                                        Choose Your Plan
                                    </h2>

                                    {planError && (
                                        <p className="planError">
                                            {planError}
                                        </p>
                                    )}

                                    <div className="plansGrid">

                                        {/* Trial */}
                                        <div className="planCard">

                                            <h3>
                                                Trial
                                            </h3>

                                            <h1>
                                                ₹2
                                            </h1>

                                            <p>
                                                1 Day Access
                                            </p>

                                            <button
                                                onClick={() =>
                                                    setSelectedPlan("trial")
                                                }
                                            >
                                                Try for ₹2
                                            </button>

                                        </div>

                                        {/* Weekly */}
                                        <div className="planCard">

                                            <h3>
                                                Weekly
                                            </h3>

                                            <strike>
                                                ₹ 99
                                            </strike>

                                            <h1>
                                                ₹49
                                            </h1>

                                            <p>
                                                7 Days Access
                                            </p>

                                            <button
                                                onClick={() =>
                                                    setSelectedPlan("weekly")
                                                }
                                            >
                                                Pay Now
                                            </button>

                                        </div>

                                        {/* Monthly */}
                                        <div className="planCard premium">

                                            <h3>
                                                Monthly
                                            </h3>

                                            <strike>
                                                ₹ 199
                                            </strike>

                                            <h1>
                                                ₹ 149
                                            </h1>

                                            <p>
                                                30 Days Access
                                            </p>

                                            <button
                                                onClick={() =>
                                                    setSelectedPlan("monthly")
                                                }
                                            >
                                                Buy Now
                                            </button>

                                        </div>

                                        {/* Yearly */}
                                        <div className="planCard">

                                            <h3>
                                                Yearly
                                            </h3>

                                            <strike>
                                                ₹ 999
                                            </strike>

                                            <h1>
                                                ₹ 799
                                            </h1>

                                            <p>
                                                365 Days Access
                                            </p>

                                            <button
                                                onClick={() =>
                                                    setSelectedPlan("yearly")
                                                }
                                            >
                                                Buy Now
                                            </button>

                                        </div>

                                    </div>
                                </>
                            )}

                        </div>
                    </div>,
                    document.body
                )
            }

        </nav>
    );
}

export default Navbar;

